import { useDrawerStatus } from '@react-navigation/drawer'
import { useNavigation } from '@react-navigation/native'
import { IS_PWA, IS_WEB } from 'constants/platform'
import React, { useEffect, useMemo, useRef } from 'react'
import { StatusBar, StyleSheet, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Box } from 'widgets/Box'
import { FloatingSearchBar } from 'widgets/FloatingUserSearchBar'
import { Icon } from 'widgets/Icon'
import { LogoButton } from 'widgets/LogoButton'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'
import { Title } from 'widgets/Title'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useNavigateBackOrHome } from 'hooks/useNavigateBackOrHome'
import { useNotifications } from 'hooks/useNotifications'
import { useUnreadMessageCount } from 'hooks/useUnreadMessageCount'

import { links } from 'utils/constants'
import { ThemeColor } from 'utils/themes/types'

import { useFocusMode } from 'contexts/FocusModeContext'
import { useLaunchpadModal } from 'contexts/LaunchpadModalContext'

import { NavMenuButton } from './types'

type MobileNavProps = {
  withBack?: boolean
  title?: string
  withChatButton?: boolean
  withLogoButton?: boolean
  navbarRightButtons?: NavMenuButton[]
  withSearch: boolean
  withExitFocusMode?: boolean
  withNotificationButton?: boolean
  withSearchButton?: boolean
}

export const MobileNav = ({
  withBack = false,
  title = '',
  withChatButton = false,
  withLogoButton,
  navbarRightButtons,
  withSearch,
  withExitFocusMode = false,
  withNotificationButton = false,
  withSearchButton = false,
}: MobileNavProps) => {
  const navigateBackOrHome = useNavigateBackOrHome()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation<any>()

  const { data: unreadMessageCount } = useUnreadMessageCount()
  const hasUnreadMessages = useRef<boolean>(unreadMessageCount ? unreadMessageCount > 0 : false)
  useEffect(() => {
    if (unreadMessageCount) {
      hasUnreadMessages.current = unreadMessageCount > 0
    }
  }, [unreadMessageCount])

  const { data: currentUser } = useCurrentUser()
  const { data: notifications } = useNotifications()
  const notificationsSeenAt = useMemo(
    () => currentUser?.meta?.notificationsLastSeenTimestamp ?? 0,
    [currentUser?.meta?.notificationsLastSeenTimestamp],
  )

  const hasUnreadNotifications = useMemo(() => {
    if (!notifications || notifications.length === 0) {
      return false
    }
    if (notificationsSeenAt < notifications[0].timestamp) {
      return true
    }

    return false
  }, [notifications, notificationsSeenAt])

  const isDrawerOpen = useDrawerStatus() === 'open'

  const launchpadModalRef = useLaunchpadModal()

  const onLogoButtonPress = () => {
    launchpadModalRef?.current?.present()
    // navigation.openDrawer()
  }

  const dimensions = useWindowDimensions()
  const isLargeScreen = dimensions.width >= 768

  const styles = StyleSheet.create({
    sideButtonsContainer: {
      width: '15%',
    },
    largeSideButtonsContainer: {
      width: '45%',
    },
    middleContainer: {
      // eslint-disable-next-line no-nested-ternary
      width: !withSearch ? (withSearchButton && withChatButton ? '40%' : '70%') : '85%',
    },
    unreadIndicator: {
      position: 'absolute',
      zIndex: 99,
      top: 4,
      right: 8,
    },
    chatUnreadIndicator: {
      position: 'absolute',
      zIndex: 99,
      top: 4,
      right: 0,
    },
  })

  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])
  const showExitButton = withExitFocusMode && isFocusMode

  const handleExitFocusMode = () => {
    focusMode.setGroup(undefined)
    navigation.navigate(links.HOME_STACK, {
      screen: links.FEED_PAGE,
    })
  }

  const exitNavButton: NavMenuButton = {
    iconName: 'x',
    onPress: handleExitFocusMode,
  }

  const navButtons = navbarRightButtons ? [...navbarRightButtons] : []
  if (showExitButton) {
    navButtons.push(exitNavButton)
  }

  const renderNavButton = (navBtn: NavMenuButton) => {
    if (navBtn.iconName) {
      return (
        <Box
          width={36}
          height={36}
          margin={{ left: withSearch ? 16 : 28 }}
          justifyContent='center'
          align='center'
          testID={`NavBarRightIcon-${navBtn.iconName}`}
          key={navBtn.iconName}
        >
          <Pressable onPress={navBtn.onPress}>
            <Icon name={`${navBtn.iconName}`} color={textColor} size={24} />
          </Pressable>
        </Box>
      )
    }
    if (navBtn.title) {
      return (
        <Box
          height={36}
          margin={{ horizontal: 'xs' }}
          justifyContent='center'
          align='center'
          testID={`NavBarRightIcon-${navBtn.title}`}
          key={navBtn.title}
        >
          <Pressable onPress={navBtn.onPress}>
            <Text color={navBtn.titleColor ?? 'v2primary'}>{navBtn.title}</Text>
          </Pressable>
        </Box>
      )
    }
    return null
  }

  const textColor: ThemeColor = isFocusMode ? 'white' : 'black'

  return (
    <Box
      flexDirection='row'
      align='center'
      padding={{
        top: IS_WEB || insets.top > 20 ? insets.top : 28,
        horizontal: !IS_WEB ? 'md' : 'sm',
        bottom: 'xs',
      }}
      height={IS_WEB && !IS_PWA ? 64 : undefined}
      zIndex={2}
      justifyContent='center'
      backgroundColor={isFocusMode ? 'v2black' : 'white'}
      testID='MobileNav'
    >
      <StatusBar
        backgroundColor='white'
        barStyle={isFocusMode ? 'light-content' : 'dark-content'}
        hidden={isDrawerOpen}
        animated
      />

      {(withBack || withLogoButton) && (
        <Box
          testID='LeftButtonsContainer'
          flexDirection='column'
          align='flex-start'
          style={styles.sideButtonsContainer}
        >
          <Box flexDirection='row' testID='LeftButtonsRow'>
            {withBack && (
              <Box
                width={36}
                height={36}
                margin={{ right: 'xs' }}
                justifyContent='center'
                align='center'
                testID='BackButtonContainer'
              >
                <Pressable onPress={navigateBackOrHome} testID='BackButton'>
                  <Icon name='arrow-left' color={textColor} size={24} />
                </Pressable>
              </Box>
            )}
            {!isLargeScreen && withLogoButton && (
              <Box
                width={36}
                height={36}
                justifyContent='center'
                align='center'
                margin={{ left: IS_WEB ? 'sm' : undefined, bottom: !IS_WEB ? 'xs' : undefined }}
                testID='LogoButtonContainer'
              >
                <LogoButton onPress={onLogoButtonPress} />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box
        testID='CenterContainer'
        style={styles.middleContainer}
        flexDirection='column'
        align={title !== 'Upspace' ? 'center' : 'flex-start'}
      >
        {!!title && title !== 'Upspace' && (
          <Title color={textColor} size='sm' bold numberOfLines={1}>
            {title}
          </Title>
        )}
        {!!title && title === 'Upspace' && (
          <Box
            margin={{
              top: 'sm',
              left:
                // eslint-disable-next-line no-nested-ternary
                !IS_WEB && dimensions.width > 375
                  ? 28
                  : withLogoButton && !isLargeScreen
                  ? 36
                  : -36,
            }}
          >
            <Icon name='upspace-word-mark' color='black' size={26} />
          </Box>
        )}
        {withSearch ? (
          <Box width='100%' flexDirection='row' justifyContent='flex-start' align='center'>
            <Box width={showExitButton ? '85%' : '100%'}>
              <FloatingSearchBar
                isNav
                searchPrompt='Find someone to chat with'
                width={dimensions.width * 0.8 + (IS_WEB ? 6 : -8)}
              />
            </Box>
            {showExitButton && renderNavButton(exitNavButton)}
          </Box>
        ) : null}
      </Box>

      {!withSearch && (
        <Box
          testID='RightButtonsContainer'
          flexDirection='column'
          align='flex-end'
          style={
            withChatButton && withSearchButton
              ? styles.largeSideButtonsContainer
              : styles.sideButtonsContainer
          }
        >
          <Box flexDirection='row' testID='RightButtonsRow'>
            {withSearchButton && (
              <Box
                margin={{ left: 28 }}
                width={36}
                height={36}
                justifyContent='center'
                align='center'
                testID='NavChatButtonContainer'
              >
                <Pressable onPress={() => navigation.navigate(links.EXPLORE_PAGE)}>
                  <Icon
                    name='discover-unselected'
                    color={!isFocusMode ? 'black' : 'white'}
                    size={24}
                  />
                </Pressable>
              </Box>
            )}

            {withChatButton && (
              <Box
                margin={{ left: 28 }}
                width={36}
                height={36}
                justifyContent='center'
                align='center'
                testID='NavChatButtonContainer'
              >
                {hasUnreadMessages.current && (
                  <Box
                    testID='ChatIcon-UnreadIndicator'
                    backgroundColor='v2secondary'
                    borderRadius='md'
                    align='center'
                    width={12}
                    height={12}
                    style={styles.chatUnreadIndicator}
                    border={{ width: 2, color: 'white' }}
                  />
                )}
                <Pressable link={links.CHAT_PAGE}>
                  <Icon name='chat-unselected' color={!isFocusMode ? 'black' : 'white'} size={24} />
                </Pressable>
              </Box>
            )}

            {withNotificationButton && (
              <Box
                margin={{ left: 28 }}
                width={36}
                height={36}
                justifyContent='center'
                align='center'
                testID='NavChatButtonContainer'
              >
                {hasUnreadNotifications && (
                  <Box
                    testID='NotificationIcon-UnreadIndicator'
                    backgroundColor='v2secondary'
                    borderRadius='md'
                    align='center'
                    width={12}
                    height={12}
                    style={styles.unreadIndicator}
                    border={{ width: 2, color: 'white' }}
                  />
                )}
                <Pressable link={links.NOTIFICATIONS_PAGE}>
                  <Icon name='bell' color={!isFocusMode ? 'black' : 'white'} size={24} />
                </Pressable>
              </Box>
            )}

            {navButtons && navButtons.map((item) => renderNavButton(item))}
          </Box>
        </Box>
      )}
    </Box>
  )
}
